import { ATTOM_AVM_INPUT_VERSION, ATTOM_AVM_LOOKUP_VERSION } from "./attom-avm.js";
import { ATTOM_OWNER_LOOKUP_VERSION } from "./attom-owner.js";
import { PIPELINE_TOOL_OUTCOMES, type PipelineToolOutcome } from "./pipeline-tool-outcomes.js";
import {
  EQUITY_FORMULA_INPUT_VERSION,
  EQUITY_FORMULA_OUTPUT_VERSION
} from "./property-equity.js";
import { PROPERTY_VALUE_LOOKUP_VERSION, ZILLOW_ZESTIMATE_INPUT_VERSION } from "./property-value.js";

const REPORTALL_OWNER_INPUT_VERSION = "reportall-owner-input-v1";
const REPORTALL_OWNER_LOOKUP_VERSION = "reportall-owner-v1";
const CLOSE_CREATE_INPUT_VERSION = "close-create-input-v1";
const CLOSE_CREATE_OUTPUT_VERSION = "close-create-v1";
const CLOSE_STATUS_INPUT_VERSION = "close-status-input-v1";
const CLOSE_STATUS_OUTPUT_VERSION = "close-status-v1";
// ponytail: duplicate of obituary-steps version strings. Importing that module here
// TDZ-cycles through the core barrel (validate-registry loads dist/index.js).
const OBITUARY_SEARCH_INPUT_VERSION = "obituary-search-input-v1";
const OBITUARY_SEARCH_OUTPUT_VERSION = "obituary-search-v1";
const OBITUARY_EVIDENCE_INPUT_VERSION = "obituary-evidence-input-v1";
const OBITUARY_EVIDENCE_OUTPUT_VERSION = "obituary-evidence-v1";
const OBITUARY_IDENTITY_INPUT_VERSION = "obituary-identity-input-v1";
const OBITUARY_IDENTITY_OUTPUT_VERSION = "obituary-identity-v1";

export const REGISTERED_TOOL_IDS = [
  "tree_ingest",
  "ecourts_auction_date",
  "ecourts_case_upsert",
  "brightdata_ecourts_auction_date",
  "reportall_owner",
  "attom_owner",
  "smartskip",
  "attom_avm",
  "zillow_zestimate",
  "ecourts_sp_plv",
  "equity_formula",
  "close_create",
  "close_status",
  "obituary_google",
  "obituary_xai",
  "obituary_evidence",
  "obituary_identity",
  "nc_intestacy_text_tree"
] as const;
export type RegisteredToolId = (typeof REGISTERED_TOOL_IDS)[number];

export const PIPELINE_TOOL_AVAILABILITIES = ["executable", "optional", "disabled", "unavailable"] as const;
export type PipelineToolAvailability = (typeof PIPELINE_TOOL_AVAILABILITIES)[number];

export type PipelineToolExecutorCapability =
  | { kind: "batch_drain"; handlerId: string; workItemHandlerId?: string }
  | { kind: "formula"; handlerId: string | null; workItemHandlerId?: string }
  | { kind: "ingest"; handlerId: string }
  | { kind: "status"; handlerId: string; workItemHandlerId?: string }
  | { kind: "none"; reason: string };

export type PipelineToolContract = {
  inputVersion: string;
  outputVersion: string;
  inputFields: readonly string[];
  outputFields: readonly string[];
};

export type PipelineToolDefinition = {
  id: RegisteredToolId;
  title: string;
  availability: PipelineToolAvailability;
  optional: boolean;
  executor: PipelineToolExecutorCapability;
  hop: string | null;
  contract: PipelineToolContract;
  outcomes: readonly PipelineToolOutcome[];
  /** Fallback-group ids that must appear in an earlier step. */
  requires: readonly string[];
  fallbackGroup: string;
};

const VENDOR_OUTCOMES = PIPELINE_TOOL_OUTCOMES;
const FORMULA_OUTCOMES = ["succeeded", "invalid"] as const satisfies readonly PipelineToolOutcome[];

function contract(
  inputVersion: string,
  outputVersion: string,
  inputFields: readonly string[],
  outputFields: readonly string[]
): PipelineToolContract {
  return { inputVersion, outputVersion, inputFields, outputFields };
}

export const PIPELINE_TOOL_REGISTRY: Record<RegisteredToolId, PipelineToolDefinition> = {
  tree_ingest: {
    id: "tree_ingest",
    title: "Tree ingest",
    availability: "executable",
    optional: false,
    executor: { kind: "ingest", handlerId: "properties.tree_ingest" },
    hop: "treeIngest",
    contract: contract("tree-ingest-input-v1", "tree-ingest-v1", ["source", "capturedAt"], ["properties", "latestScrapeAt"]),
    outcomes: VENDOR_OUTCOMES,
    requires: [],
    fallbackGroup: "ingest"
  },
  ecourts_auction_date: {
    id: "ecourts_auction_date",
    title: "eCourts retrieval",
    availability: "executable",
    optional: false,
    executor: {
      kind: "batch_drain",
      handlerId: "properties.auction_date_refresh",
      workItemHandlerId: "properties.auction_date_item"
    },
    hop: "auctionDateRefresh",
    contract: contract(
      "ecourts-retrieval-input-v1",
      "ecourts-retrieval-v1",
      ["workItemId", "caseNumber", "county"],
      ["caseId", "events", "documents", "receipt", "observations"]
    ),
    outcomes: VENDOR_OUTCOMES,
    requires: [],
    fallbackGroup: "auction_date"
  },
  ecourts_case_upsert: {
    id: "ecourts_case_upsert",
    title: "eCourts upsert",
    availability: "executable",
    optional: false,
    executor: {
      kind: "formula",
      handlerId: "properties.ecourts_upsert",
      workItemHandlerId: "properties.ecourts_upsert_item"
    },
    hop: "ecourtsUpsert",
    contract: contract(
      "ecourts-case-upsert-input-v1",
      "ecourts-case-upsert-v1",
      ["workItemId", "caseNumber", "county"],
      ["caseId", "projectionVersion", "auctionDate"]
    ),
    outcomes: FORMULA_OUTCOMES,
    requires: ["auction_date"],
    fallbackGroup: "ecourts_upsert"
  },
  brightdata_ecourts_auction_date: {
    id: "brightdata_ecourts_auction_date",
    title: "Bright Data auction date",
    availability: "unavailable",
    optional: true,
    executor: { kind: "none", reason: "Bright Data is deprecated." },
    hop: "auctionDateRefresh",
    contract: contract(
      "ecourts-auction-date-input-v1",
      "ecourts-auction-date-v1",
      ["workItemId", "caseNumber", "county"],
      ["auctionDate", "saleState", "checkedAt"]
    ),
    outcomes: VENDOR_OUTCOMES,
    requires: [],
    fallbackGroup: "auction_date"
  },
  reportall_owner: {
    id: "reportall_owner",
    title: "ReportAll owner",
    availability: "executable",
    optional: false,
    executor: {
      kind: "batch_drain",
      handlerId: "properties.reportall",
      workItemHandlerId: "properties.reportall_item"
    },
    hop: "reportall",
    contract: contract(
      REPORTALL_OWNER_INPUT_VERSION,
      REPORTALL_OWNER_LOOKUP_VERSION,
      ["workItemId", "street", "city", "state", "postalCode", "county"],
      ["ownerRaw", "parcelId", "mailingStreet", "mailingCity", "mailingState", "mailingZip"]
    ),
    outcomes: VENDOR_OUTCOMES,
    requires: [],
    fallbackGroup: "owner"
  },
  attom_owner: {
    id: "attom_owner",
    title: "ATTOM owner",
    availability: "executable",
    optional: true,
    executor: {
      kind: "batch_drain",
      handlerId: "properties.attom_owner",
      workItemHandlerId: "properties.attom_owner_item"
    },
    hop: "attomOwner",
    contract: contract(
      "attom-owner-input-v1",
      ATTOM_OWNER_LOOKUP_VERSION,
      ["workItemId", "street", "city", "state", "postalCode"],
      ["owners", "parcelId", "attomId", "ownerMailingAddress"]
    ),
    outcomes: VENDOR_OUTCOMES,
    requires: [],
    fallbackGroup: "owner"
  },
  smartskip: {
    id: "smartskip",
    title: "SmartSkip",
    availability: "executable",
    optional: false,
    executor: { kind: "batch_drain", handlerId: "properties.smartskip" },
    hop: "smartskip",
    contract: contract(
      "smartskip-input-v1",
      "smartskip-v1",
      ["workItemId", "firstName", "lastName"],
      ["phones", "skipPayload"]
    ),
    outcomes: VENDOR_OUTCOMES,
    requires: ["owner"],
    fallbackGroup: "skip"
  },
  attom_avm: {
    id: "attom_avm",
    title: "ATTOM AVM",
    availability: "executable",
    optional: true,
    executor: {
      kind: "batch_drain",
      handlerId: "properties.avm",
      workItemHandlerId: "properties.avm_item"
    },
    hop: "avm",
    contract: contract(
      ATTOM_AVM_INPUT_VERSION,
      ATTOM_AVM_LOOKUP_VERSION,
      ["workItemId", "street", "city", "state", "postalCode"],
      ["attomAvm"]
    ),
    outcomes: VENDOR_OUTCOMES,
    requires: ["skip"],
    fallbackGroup: "value"
  },
  zillow_zestimate: {
    id: "zillow_zestimate",
    title: "Zillow Zestimate",
    availability: "executable",
    optional: false,
    executor: {
      kind: "batch_drain",
      handlerId: "properties.zillow",
      workItemHandlerId: "properties.zillow_item"
    },
    hop: "zillow",
    contract: contract(
      ZILLOW_ZESTIMATE_INPUT_VERSION,
      PROPERTY_VALUE_LOOKUP_VERSION,
      ["workItemId", "street", "city", "state", "postalCode"],
      ["zestimate"]
    ),
    outcomes: VENDOR_OUTCOMES,
    requires: ["skip"],
    fallbackGroup: "value"
  },
  ecourts_sp_plv: {
    id: "ecourts_sp_plv",
    title: "eCourts analysis",
    availability: "executable",
    optional: false,
    executor: {
      kind: "batch_drain",
      handlerId: "properties.plv",
      workItemHandlerId: "properties.plv_item"
    },
    hop: "plv",
    contract: contract(
      "ecourts-analysis-input-v1",
      "ecourts-analysis-v1",
      ["workItemId", "caseNumber", "county"],
      ["caseId", "observations"]
    ),
    outcomes: VENDOR_OUTCOMES,
    requires: [],
    fallbackGroup: "plv"
  },
  equity_formula: {
    id: "equity_formula",
    title: "Equity",
    availability: "executable",
    optional: false,
    executor: {
      kind: "formula",
      handlerId: "properties.equity",
      workItemHandlerId: "properties.equity_item"
    },
    hop: "equity",
    contract: contract(
      EQUITY_FORMULA_INPUT_VERSION,
      EQUITY_FORMULA_OUTPUT_VERSION,
      ["marketValue", "plv", "marketValuePct", "loanValuePct"],
      ["estimatedEquity"]
    ),
    outcomes: FORMULA_OUTCOMES,
    requires: ["value", "plv"],
    fallbackGroup: "equity"
  },
  close_create: {
    id: "close_create",
    title: "Close.io create",
    availability: "executable",
    optional: false,
    executor: {
      kind: "batch_drain",
      handlerId: "properties.close_create",
      workItemHandlerId: "properties.close_create_item"
    },
    hop: "closeCreate",
    contract: contract(
      CLOSE_CREATE_INPUT_VERSION,
      CLOSE_CREATE_OUTPUT_VERSION,
      ["workItemId", "correlationKey", "deceased", "estimatedEquity"],
      ["closeLeadId"]
    ),
    outcomes: VENDOR_OUTCOMES,
    requires: ["equity"],
    fallbackGroup: "close"
  },
  close_status: {
    id: "close_status",
    title: "Close.io status",
    availability: "executable",
    optional: false,
    executor: {
      kind: "status",
      handlerId: "properties.close_status",
      workItemHandlerId: "properties.close_status_item"
    },
    hop: "closeStatus",
    contract: contract(
      CLOSE_STATUS_INPUT_VERSION,
      CLOSE_STATUS_OUTPUT_VERSION,
      ["workItemId", "closeLeadId", "correlationKey", "status"],
      ["closeLeadId", "conveyorState"]
    ),
    outcomes: VENDOR_OUTCOMES,
    requires: ["close"],
    fallbackGroup: "close_status"
  },
  obituary_google: {
    id: "obituary_google",
    title: "Obituary search",
    availability: "executable",
    optional: true,
    executor: {
      kind: "batch_drain",
      handlerId: "properties.obituary",
      workItemHandlerId: "properties.obituary_search"
    },
    hop: "obituary",
    contract: contract(
      OBITUARY_SEARCH_INPUT_VERSION,
      OBITUARY_SEARCH_OUTPUT_VERSION,
      ["workItemId", "identityPacket", "provider"],
      ["candidateUrls", "nextStage", "exhausted"]
    ),
    outcomes: VENDOR_OUTCOMES,
    requires: ["equity"],
    fallbackGroup: "obituary"
  },
  obituary_xai: {
    id: "obituary_xai",
    title: "Obituary xAI search",
    availability: "executable",
    optional: true,
    executor: {
      kind: "batch_drain",
      handlerId: "properties.obituary",
      workItemHandlerId: "properties.obituary_search"
    },
    hop: "obituary",
    contract: contract(
      OBITUARY_SEARCH_INPUT_VERSION,
      OBITUARY_SEARCH_OUTPUT_VERSION,
      ["workItemId", "identityPacket", "provider"],
      ["candidateUrls", "nextStage", "exhausted"]
    ),
    outcomes: VENDOR_OUTCOMES,
    requires: ["equity"],
    fallbackGroup: "obituary"
  },
  obituary_evidence: {
    id: "obituary_evidence",
    title: "Obituary evidence",
    availability: "executable",
    optional: true,
    executor: { kind: "formula", handlerId: null },
    hop: null,
    contract: contract(
      OBITUARY_EVIDENCE_INPUT_VERSION,
      OBITUARY_EVIDENCE_OUTPUT_VERSION,
      ["workItemId", "identityPacket", "candidateUrls"],
      ["sourceCount", "sourceUrls"]
    ),
    outcomes: VENDOR_OUTCOMES,
    requires: ["obituary"],
    fallbackGroup: "obituary_evidence"
  },
  obituary_identity: {
    id: "obituary_identity",
    title: "Obituary identity",
    availability: "executable",
    optional: true,
    executor: { kind: "formula", handlerId: null },
    hop: null,
    contract: contract(
      OBITUARY_IDENTITY_INPUT_VERSION,
      OBITUARY_IDENTITY_OUTPUT_VERSION,
      ["workItemId", "identityPacket", "sources"],
      ["decision", "resolverDecision", "reviewReasons"]
    ),
    outcomes: VENDOR_OUTCOMES,
    requires: ["obituary_evidence"],
    fallbackGroup: "obituary_identity"
  },
  nc_intestacy_text_tree: {
    id: "nc_intestacy_text_tree",
    title: "NC intestacy",
    availability: "executable",
    optional: true,
    executor: {
      kind: "formula",
      handlerId: "properties.intestacy",
      workItemHandlerId: "properties.intestacy_item"
    },
    hop: "intestacy",
    contract: contract(
      "nc-intestacy-input-v1",
      "nc-intestacy-v1",
      ["obituaryMatchStatus", "familyGraph"],
      ["intestacyTree", "intestacyStatus"]
    ),
    outcomes: FORMULA_OUTCOMES,
    requires: ["obituary"],
    fallbackGroup: "intestacy"
  }
};

export function isRegisteredToolId(value: unknown): value is RegisteredToolId {
  return typeof value === "string" && (REGISTERED_TOOL_IDS as readonly string[]).includes(value);
}

export function pipelineToolDefinition(toolId: string): PipelineToolDefinition | null {
  return isRegisteredToolId(toolId) ? PIPELINE_TOOL_REGISTRY[toolId] : null;
}

export function pipelineToolTitle(toolId: string): string {
  return pipelineToolDefinition(toolId)?.title ?? toolId.replaceAll("_", " ");
}

export function pipelineToolsInFallbackGroup(group: string): RegisteredToolId[] {
  return REGISTERED_TOOL_IDS.filter((id) => PIPELINE_TOOL_REGISTRY[id].fallbackGroup === group);
}

/** True when any step in the workflow carries a tool from the fallback group. */
export function workflowUsesFallbackGroup(
  document: { steps: ReadonlyArray<{ tools: readonly string[] }> },
  group: string
): boolean {
  for (const step of document.steps) {
    for (const toolId of step.tools) {
      if (!isRegisteredToolId(toolId)) continue;
      if (PIPELINE_TOOL_REGISTRY[toolId].fallbackGroup === group) return true;
    }
  }
  return false;
}

/** Next executable tool in the same fallback group. Skips unavailable/deprecated providers. */
export function selectConfiguredFallback(input: {
  toolId: string;
  runtime?: ReadonlyArray<{ toolId: string; state?: string | null }>;
}): RegisteredToolId | null {
  const def = pipelineToolDefinition(input.toolId);
  if (!def) return null;
  const group = pipelineToolsInFallbackGroup(def.fallbackGroup);
  const current = group.indexOf(def.id);
  const rest = current >= 0 ? group.slice(current + 1) : group.filter((id) => id !== def.id);
  for (const candidate of rest) {
    const runtime = input.runtime?.find((row) => row.toolId === candidate);
    const availability = pipelineToolAvailability(candidate, runtime);
    if (availability === "unavailable" || availability === "disabled" || availability === "unknown") continue;
    if (/bright_?data/i.test(candidate)) continue;
    return candidate;
  }
  return null;
}

export function pipelineToolAvailability(
  toolId: string,
  runtime?: { state?: string | null }
): PipelineToolAvailability | "unknown" {
  const def = pipelineToolDefinition(toolId);
  if (!def) return "unknown";
  if (def.availability === "unavailable" || def.availability === "disabled") return def.availability;
  if (runtime?.state === "not_configured" || runtime?.state === "missing_credential" || runtime?.state === "disabled") {
    return "disabled";
  }
  return def.availability;
}

export type PipelineStepLaneAvailability = "executable" | "unavailable" | "disabled" | "terminal";

export function pipelineStepLaneAvailability(
  step: { kind: string; tools: readonly string[] },
  runtimeTools?: ReadonlyArray<{ toolId: string; state: string }>
): PipelineStepLaneAvailability {
  if (step.kind === "terminal") return "terminal";
  if (step.tools.length === 0) return "unavailable";
  const states = step.tools.map((id) =>
    pipelineToolAvailability(id, runtimeTools?.find((row) => row.toolId === id))
  );
  if (states.every((state) => state === "unavailable" || state === "disabled" || state === "unknown")) {
    return states.includes("disabled") && !states.includes("unavailable") ? "disabled" : "unavailable";
  }
  return "executable";
}

export function pipelineToolExecutorHandlerId(toolId: RegisteredToolId): string | null {
  const executor = PIPELINE_TOOL_REGISTRY[toolId].executor;
  if (executor.kind === "batch_drain" || executor.kind === "status" || executor.kind === "ingest") {
    return executor.handlerId;
  }
  if (executor.kind === "formula") return executor.handlerId;
  return null;
}

/** Actionable registry issues for a structurally parsed workflow. Empty = valid. */
export function pipelineWorkflowRegistryIssues(document: {
  steps: ReadonlyArray<{ id: string; kind: string; tools: readonly string[] }>;
}): string[] {
  const issues: string[] = [];
  const satisfied = new Set<string>();
  for (const step of document.steps) {
    const groups = new Set<string>();
    for (const toolId of step.tools) {
      if (!isRegisteredToolId(toolId)) {
        issues.push(
          `Unknown tool id "${toolId}". It is not in the pipeline tool registry.`
        );
        continue;
      }
      const def = PIPELINE_TOOL_REGISTRY[toolId];
      groups.add(def.fallbackGroup);
      for (const group of def.requires) {
        if (satisfied.has(group)) continue;
        if (!workflowUsesFallbackGroup(document, group)) continue;
        const needed = pipelineToolsInFallbackGroup(group);
        const named = needed.length ? needed.join(" or ") : group;
        issues.push(
          `Impossible dependency: ${toolId} is before ${named}, which it requires. Place ${named} in an earlier step.`
        );
      }
      satisfied.add(def.fallbackGroup);
    }
    const vendorGroups = [...groups].filter((group) =>
      pipelineToolsInFallbackGroup(group).some((id) => {
        const kind = PIPELINE_TOOL_REGISTRY[id].executor.kind;
        return kind !== "formula" && kind !== "none";
      })
    );
    if (vendorGroups.length > 1) {
      issues.push(
        `Unsupported reorder: step "${step.id}" mixes ${[...groups].join(" and ")} tools. Fallbacks must share a capability; keep each group on its own step.`
      );
    }
    if (step.kind !== "terminal" && step.tools.length === 0) {
      issues.push(
        `Step "${step.id}" has no tools. Attach a registered tool or mark the step as a terminal sink.`
      );
    }
  }
  return [...new Set(issues)];
}

export function executableBatchDrainHandlerIds(): string[] {
  const ids: string[] = [];
  for (const toolId of REGISTERED_TOOL_IDS) {
    const def = PIPELINE_TOOL_REGISTRY[toolId];
    if (def.availability === "unavailable" || def.availability === "disabled") continue;
    if (def.executor.kind === "batch_drain" || def.executor.kind === "status" || def.executor.kind === "formula") {
      if (def.executor.handlerId) ids.push(def.executor.handlerId);
    }
  }
  return [...new Set(ids)];
}
